import React from "react";
import Head from "next/head";
import Link from "next/link";

import ModelOfferCard from "components/cards/ModelOfferCard";
import buildCanonicalTagHref from "utils/seo/buildCanonicalTagHref";

import config from "../../../../config";

export function BMW2erHead({
  modelPageUrl,
}) {
  return (
    <Head>
      <meta
        name="description"
        content="BMW 2er Active Tourer, BMW 2er Gran Tourer,
      Coupé & Cabrio. Alle wichtigen Informationen und Angebote
      ✓
      Der BMW 2er schon ab 169 €
      Leasingrate."
      />
      <meta
        name="robots"
        content={config.robotsContent}
      />
      <link rel="canonical" href={buildCanonicalTagHref(modelPageUrl || "")} />
      <title>
        BMW 2er Leasing ohne Anzahlung | Familienvan, Coupé oder Cabrio
      </title>
    </Head>
  );
}

const toc = [
  { topic: "Welche 2er BMW gibt es?", anchor: "welche-2er-bmw-gibt-es" },
  {
    topic: "Worin bestehen die Unterschiede zwischen den Modellvarianten?",
    anchor: "worin-bestehen-die-unterschiede-zwischen-den-modellvarianten",
  },
  { topic: "Was kostet ein BMW 2er?", anchor: "was-kostet-ein-bmw-2er" },
  {
    topic: "Warum sind 2er BMW so günstig?",
    anchor: "warum-sind-2er-bmw-so-guenstig",
  },
  {
    topic: "Der BMW 2er und seine Anhängelast - wie viel kann er ziehen?",
    anchor: "der-bmw-2er-und-seine-anhaengelast-wie-viel-kann-er-ziehen",
  },
  {
    topic: "BMW 2er hybrid mit umweltschonendem Antrieb",
    anchor: "bmw-2er-hybrid-mit-umweltschonendem-antrieb",
  },
  {
    topic: "Verschiedene Ausstattungspakete für individuelle Vorlieben",
    anchor: "verschiedene-ausstattungspakete-fuer-individuelle-vorlieben",
  },
  {
    topic: "Was gehört zur Luxury Ausstattung beim BMW 2er?",
    anchor: "was-gehoert-zur-luxury-ausstattung-beim-bmw-2er",
  },
  {
    topic: "BMW 2er Facelift im Jahre 2017",
    anchor: "bmw-2er-facelift-im-jahre-2017",
  },
  {
    topic: "BMW 2er Gran Tourer und BMW 2er Active Tourer im Test",
    anchor: "bmw-2er-gran-tourer-und-bmw-2er-active-tourer-im-test",
  },
  { topic: "Fazit", anchor: "fazit" },
];

function BMW2er({ hasOffers, modelTypes }) {
  return (
    <div className="page-container page-container--tight px-5 mb-1">
      <h2 className="headline--4 mb-4">
        BMW 2er - spannende Details für die richtige Typwahl
      </h2>
      <p className="paragraph--large mb-5 lg:mb-8 text-grey-darker">
        Die 2er BMW Modelle - eine einzigartige Baureihe für Autofahrer mit hohem
        Anspruch
      </p>
      <ol className="mb-5 lg:mb-8">
        {toc.map(({ topic, anchor }) => (
          <li key={anchor} className="text-secondary font-bold">
            <Link href={`#${anchor}`} className="link link--secondary">
              {topic}
            </Link>
          </li>
        ))}
      </ol>
      <div className="mb-10 lg:mb-12">
        <p className="mb-4">
          Die 2er Baureihe von BMW ist bei Autofahrern beliebt. Aufgrund der
          unterschiedlichen Modelle und Ausstattungen fällt die Wahl allerdings
          zunächst schwer. Häufig fehlen Informationen oder die Unterschiede
          zwischen den Modellen sind nicht klar. Worin beispielsweise
          unterscheiden sich der
          {" "}
          <Link
            href="/suche?marke=BMW&modell=2er%20Active%20Tourer"
            className="link link--secondary"
          >
            BMW 2er Active Tourer
          </Link>
          {" "}
          und der
          {" "}
          <Link
            href="/suche?marke=BMW&modell=2er%20Gran%20Tourer"
            className="link link--secondary"
          >
            BMW 2er Gran Tourer
          </Link>
          ?
        </p>
        <p>
          Die Verunsicherung, die hierdurch entsteht, ist verständlich. Sie lässt
          sich jedoch durch die ausführlichen Informationen in folgendem Beitrag
          beheben.
        </p>
      </div>

      {/* Welche 2er BMW gibt es? */}
      <div id="welche-2er-bmw-gibt-es" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">Welche 2er BMW gibt es?</h3>

        <table className="responsive-table mb-10" role="table">
          <thead>
            <tr role="row">
              <th role="columnheader">Fahrzeugtyp</th>
              <th role="columnheader">Preise</th>
              <th role="columnheader">Motorisierung</th>
              <th role="columnheader">Hubraum</th>
              <th role="columnheader">Verbrauch</th>
            </tr>
          </thead>
          <tbody>
            <tr role="row">
              <td data-label="Fahrzeugtyp" role="cell">
                Coupe
              </td>
              <td data-label="Preise" role="cell">
                30.700 - 52.500 Euro
              </td>
              <td data-label="Motorisierung" role="cell">
                136 - 410 PS
              </td>
              <td data-label="Hubraum" role="cell">
                1.499 - 2.998
              </td>
              <td data-label="Verbrauch" role="cell">
                4,5 - 8,0 l/100 km
              </td>
            </tr>
            <tr role="row">
              <td data-label="Fahrzeugtyp" role="cell">
                Cabrio
              </td>
              <td data-label="Preise" role="cell">
                35.350 - 56.450 Euro
              </td>
              <td data-label="Motorisierung " role="cell">
                136 - 340 PS
              </td>
              <td data-label="Hubraum" role="cell">
                1.499 - 2.998
              </td>
              <td data-label="Verbrauch" role="cell">
                4,8 - 8,3 l/100 km
              </td>
            </tr>
            <tr role="row">
              <td data-label="Fahrzeugtyp" role="cell">
                Active Tourer
              </td>
              <td data-label="Preise" role="cell">
                27.950 - 46.950 Euro
              </td>
              <td data-label="Motorisierung" role="cell">
                109 - 231 PS
              </td>
              <td data-label="Hubraum" role="cell">
                1.496 - 1.998
              </td>
              <td data-label="Verbrauch" role="cell">
                3,9 - 6,2 l/100 km
              </td>
            </tr>
            <tr role="row">
              <td data-label="Fahrzeugtyp" role="cell">
                Gran Tourer
              </td>
              <td data-label="Preise" role="cell">
                29.200 - 47.800 Euro
              </td>
              <td data-label="Motorisierung" role="cell">
                109 - 192 PS
              </td>
              <td data-label="Hubraum" role="cell">
                1.496 - 1.998
              </td>
              <td data-label="Verbrauch" role="cell">
                4,1 - 5,7 l/100 km
              </td>
            </tr>
          </tbody>
        </table>

        <p className="mb-4">
          Innerhalb seiner 2er Baureihe präsentiert der Automobilhersteller aus
          München aktuell die folgenden vier Fahrzeugtypen:
        </p>
        <ul className="mb-4">
          <li>
            das leistungsstarke
            {" "}
            <b>BMW 2er Coupé</b>
          </li>
          <li>
            das schnittige
            {" "}
            <b>BMW 2er Cabrio</b>
          </li>
          <li>
            den großräumigen
            {" "}
            <b>BMW 2er Active Tourer</b>
          </li>
          <li>
            den
            {" "}
            <b>BMW 2er Gran Tourer</b>
          </li>
        </ul>
        <p className="mb-4">
          Die BMW 2er Baureihe ist insbesondere für Personen, die ein Familienauto
          oder einen Sportsvan mit Stufenheck suchen, aber auch für erfahrene
          Autofahrer mit hohen Qualitätsansprüchen bestens geeignet.
        </p>
        <p>
          Wer ein leistungsstarkes Coupé mit sportlichem Schrägheck liebt, findet
          genauso das passende Modell wie ein Interessent, der einen großräumigen
          Tourer mit viel Platz und Stauraum für die Familie sucht.
        </p>
      </div>

      {/* Worin bestehen die Unterschiede zwischen den Modellvarianten? */}
      <div
        id="worin-bestehen-die-unterschiede-zwischen-den-modellvarianten"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          Worin bestehen die Unterschiede zwischen den Modellvarianten?
        </h3>
        <p>
          Der wesentliche Unterschied zwischen den vier Modellvarianten ist die
          Bauart, vom Coupe mit Frontantrieb über das Cabrio bis hin zu den zwei
          Tourer - Modellen (mit Allradantrieb verfügbar).
        </p>
        <picture>
          <source
            srcSet="/images/seo/null-leasing-bmw2er-modelle.webp, /images/seo/null-leasing-bmw2er-modelle@2x.webp 2x"
            type="image/webp"
          />
          <source
            srcSet="/images/seo/null-leasing-bmw2er-modelle.jpg, /images/seo/null-leasing-bmw2er-modelle@2x.jpg 2x"
            type="image/jpeg"
          />
          <img
            src="/images/seo/null-leasing-bmw2er-modelle.jpg"
            srcSet="/images/seo/null-leasing-bmw2er-modelle.jpg, /images/seo/null-leasing-bmw2er-modelle@2x.jpg 2x"
            alt="Die BMW 2er Modelle"
            title="Die BMW 2er Modelle"
            className="block mb-5"
          />
        </picture>

        <p className="mb-4">
          Alle Fahrzeugtypen gibt es in verschiedenen Ausführungen. So können sich
          Autofahrer beispielsweise zwischen dem dynamischen BMW 2er Active Tourer
          Advantage und dem sportlichen BMW 2er Active Tourer Sport Line in der
          Kompaktklasse entscheiden. Alle Modelle können zu attraktiven
          Monatsraten geleast werden.
        </p>
        <p>
          Der
          {" "}
          <b>Active Tourer Advantage</b>
          {" "}
          zeichnet sich unter anderem durch die
          dynamische Front und ein hochwertiges Interieur aus. Der
          {" "}
          <b>Active Tourer Sport Line</b>
          {" "}
          mit seinem athletischen Lifestyle ist
          mit LED Frontscheinwerfern und Leichtmetallrädern im
          Doppelspeichendesign ausgestattet.
        </p>
      </div>

      {/* Was kostet ein BMW 2er? */}
      <div id="was-kostet-ein-bmw-2er" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">Was kostet ein BMW 2er?</h3>
        <p className="mb-4 text-secondary font-bold">28.000 - 53.000 Euro</p>
        <p className="mb-4">
          oder alternativ
          {" "}
          <b>ab 169 Euro monatliche Leasingrate</b>
          .
        </p>

        {hasOffers && modelTypes.length > 0 && (
          <div className="mb-6 mt-10 lg:mt-12">
            <div className="page-container page-container--tight px-1 md:px-3 flex flex-wrap">
              {modelTypes.map(
                ({
                  brand,
                  imagePath,
                  pageUrl,
                  title,
                  minLeasingRate,
                  hasOffers,
                  cheapestProduct,
                }) => (
                  <ModelOfferCard
                    key={pageUrl}
                    brand={brand}
                    imagePath={imagePath}
                    pageUrl={pageUrl}
                    title={title}
                    minLeasingRate={minLeasingRate}
                    hasOffers={hasOffers}
                    cheapestProduct={cheapestProduct}
                  />
                ),
              )}
            </div>
          </div>
        )}

        <p className="mb-2">
          Die Preise für die beliebten Modelle der BMW 2er Baureihe sind
          insbesondere vom Fahrzeugtyp abhängig. Sie reichen je nach Ausstattung
          und Motorleistung von knapp 28.000 Euro für den BMW 2er Active Tourer
          bis hin zu etwa 53.000 Euro für das PS-starke Coupe.
        </p>
        <p>
          Alternativ zum Kauf können sämtliche Modelle der 2er BMW Reihe zu fairen
          und günstigen monatlichen Raten geleast werden.
        </p>
      </div>

      {/* Warum sind 2er BMW so günstig? */}
      <div id="warum-sind-2er-bmw-so-guenstig" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">
          Warum sind 2er BMW so günstig?
        </h3>
        <p className="mb-4">
          Viele Autofahrer sind erstaunt, dass sie ein so beliebtes und
          hochwertiges Fahrzeug wie den 2er BMW schon für
          {" "}
          <b>unter 30.000 Euro</b>
          {" "}
          bzw. schon ab
          {" "}
          <b>166 Euro Leasingrate</b>
          {" "}
          pro Monat erwerben können.
          Insbesondere aufgrund schlanker interner Prozesse sowie Strukturen
          können im Neuwagenbereich echte Deals angeboten werden.
        </p>
        <p>
          In dem Zusammenhang gibt es bei BMW direkt, aber natürlich auch hier auf
          Null-Leasing.com einen großen Bestand an sofort verfügbaren Neu-,
          Vorführ- und Jahreswagen. Die aktuellen Leasingkonditionen sind
          attraktiv und führen zu einer moderaten monatlichen Belastung in Form
          der Leasingrate.
        </p>
      </div>

      {/* Der BMW 2er und seine Anhängelast - wie viel kann er ziehen? */}
      <div
        id="der-bmw-2er-und-seine-anhaengelast-wie-viel-kann-er-ziehen"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          Der BMW 2er und seine Anhängelast - wie viel kann er ziehen?
        </h3>
        <p className="mb-4">
          Die zulässige Anhängelast der verschiedenen Fahrzeugtypen der 2er BMW
          Reihe sieht wie folgt aus:
        </p>
        <ul className="mb-6">
          <li>BMW 2er Coupé: gebremst 1.300 kg, ungebremst 680 kg</li>
          <li>BMW 2er Gran Tourer: gebremst 1.100 kg, ungebremst 730 kg</li>
          <li>BMW 2er Cabrio: gebremst 1.300 kg, ungebremst 750 kg</li>
          <li>BMW 2er Active Tourer: gebremst 1.100 kg, ungebremst 695 kg</li>
        </ul>
        <picture>
          <source
            srcSet="/images/seo/null-leasing-anhaengelast.webp, /images/seo/null-leasing-anhaengelast@2x.webp 2x"
            type="image/webp"
          />
          <source
            srcSet="/images/seo/null-leasing-anhaengelast.jpg, /images/seo/null-leasing-anhaengelast@2x.jpg 2x"
            type="image/jpeg"
          />
          <img
            src="/images/seo/null-leasing-anhaengelast.jpg"
            srcSet="/images/seo/null-leasing-anhaengelast.jpg, /images/seo/null-leasing-anhaengelast@2x.jpg 2x"
            alt="2er BMW - gebremste und ungebremste Anhängelast"
            title="2er BMW - gebremste und ungebremste Anhängelast"
            className="block"
          />
        </picture>
      </div>

      {/* BMW 2er hybrid mit umweltschonendem Antrieb */}
      <div
        id="bmw-2er-hybrid-mit-umweltschonendem-antrieb"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          BMW 2er hybrid mit umweltschonendem Antrieb
        </h3>
        <p className="mb-4">
          BMW bietet mittlerweile auch in der Volumenklasse einen umweltschonenden
          Hybrid-Antrieb beim
          {" "}
          <b>BMW 225xe iPerformance Active Tourer</b>
          {" "}
          an. Die
          interessanten Daten zum Fahrzeug lauten:
        </p>
        <ul>
          <li>Grundpreis: 44.900 Euro</li>
          <li>Leistung: 224 PS</li>
          <li>Hubraum: 1.499 ccm</li>
          <li>
            EcoTest - Durchschnittsverbrauch: 6,3 Liter Benzin plus 4,6 kWh
            elektrische Energie auf 100 Kilometer
          </li>
        </ul>
      </div>

      {/* Verschiedene Ausstattungspakete für individuelle Vorlieben */}
      <div
        id="verschiedene-ausstattungspakete-fuer-individuelle-vorlieben"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          Verschiedene Ausstattungspakete für individuelle Vorlieben
        </h3>
        <p className="mb-4">
          Alle vier Typen der 2er Serie werden mit
          {" "}
          <b>verschiedenen Ausstattungsvarianten</b>
          {" "}
          bzw. -paketen angeboten, wie
          zum Beispiel dem „Business-Paket“, „Exklusiv-Paket“ und dem
          „Entertainment-Paket“. Im Entertainment-Paket des BMW 2er Active Tourer
          Advantage sind beispielsweise die folgenden Bestandteile enthalten:
        </p>
        <ul className="mb-4">
          <li>Apple CarPlay® Vorbereitung</li>
          <li>DAB-Tuner (DAB+ fähig)</li>
          <li>Harman Kardon Surround Sound System</li>
          <li>CD-Laufwerk</li>
        </ul>
        <p>
          Zusätzlich zur Serienausstattung kann bei dieser Kompaktklasse also noch
          einiges an Sonderausstattung ergänzt werden, sodass ein 2er BMW keine
          Wünsche offen lassen muss. Multifunktionslenkrad, Audiosystem,
          Rückfahrkamera, Freisprechanlage und vieles mehr sind im 2er BMW Gang
          und Gäbe.
        </p>
      </div>

      {/* Was gehört zur Luxury Ausstattung beim BMW 2er? */}
      <div
        id="was-gehoert-zur-luxury-ausstattung-beim-bmw-2er"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          Was gehört zur Luxury Ausstattung beim BMW 2er?
        </h3>
        <p className="mb-4">
          Für Autofahrer mit höchsten Qualitätsansprüchen bietet BMW in seiner 2er
          Modellreihe eine
          {" "}
          <b>Luxury Ausstattung</b>
          {" "}
          an. So enthält beispielsweise
          das BMW 2er Coupé im Exklusiv-Paket die folgenden hochwertigen
          Ausstattungsmerkmale:
        </p>
        <ul>
          <li>Elektrisches Glasdach</li>
          <li>Elektrische Sitzverstellung inklusive Memory-Funktion</li>
          <li>Metallic-Lackierung</li>
          <li>Sonnenschutzverglasung</li>
        </ul>
      </div>

      {/* BMW 2er Facelift im Jahre 2017 */}
      <div id="bmw-2er-facelift-im-jahre-2017" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">
          BMW 2er Facelift im Jahre 2017
        </h3>
        <p>
          Im Jahre 2017 gab es bei der 2er Reihe von BMW ein sogenanntes Facelift,
          welches allerdings sehr moderat ausfiel. Die auffallendsten Änderungen
          sind eine neue Leuchtgrafik bei den Bi-LED-Scheinwerfern. Zugleich sind
          die Scheinwerfer etwas näher an den Nieren platziert. Eine weitere
          Änderung ist die Vergrößerung der unteren Lufteinlässe.
        </p>
      </div>

      {/* BMW 2er Gran Tourer und BMW 2er Active Tourer im Test */}
      <div
        id="bmw-2er-gran-tourer-und-bmw-2er-active-tourer-im-test"
        className="pt-2 mb-10 lg:mb-12"
      >
        <h3 className="headline--6 mb-5 lg:mb-6">
          BMW 2er Gran Tourer und BMW 2er Active Tourer im Test
        </h3>
        <p className="mb-4">
          Sowohl der BMW 2er Gran Tourer als auch der Active Tourer aus der 2er
          BMW Reihe wurden bereits getestet. Zu den wichtigsten Testergebnissen
          gehört, dass diese Fahrzeugtypen der 2er Baureihe durch gute
          Fahrleistungen und ein sportliches Fahrverhalten überzeugen. Die
          Emissionswerte des Active Tourer bewegen sich zwischen kombiniert 129
          bis 123 g/km. Der Kraftstoffverbrauch beträgt kombiniert auf 100 km
          zwischen 5,4 bis 5,7 Liter.
        </p>
        <p>
          Der große Fahrspaß wird unter anderem durch das Automatikgetriebe mit
          kurzen Ansprechzeiten unterstützt. Sehr positiv werden das Komfortniveau
          und das gute Platzangebot im Frontbereich erwähnt. Die
          Sicherheitsausstattung ist zeitgemäß und gegen Aufpreis stehen einige
          Assistenzsysteme zur Verfügung. Im ADAC Crashtest erreicht der BMW 2er
          Active Tourer trotz Schwächen beim Frontcrash und Fußgängerschutz 5
          Sterne.
        </p>
      </div>

      {/* Fazit */}
      <div id="fazit" className="pt-2 mb-10 lg:mb-12">
        <h3 className="headline--6 mb-5 lg:mb-6">Fazit</h3>
        <p className="mb-4">
          Die Fahrzeugtypen der BMW 2er Reihe beinhalten weiterhin den hohen
          Qualitätsstandard, den BMW-Fahrer gewohnt sind. Mit den vier Grundtypen
          werden vor allem Familien sowie erfahrene Autofahrer mit hohen
          Qualitätsansprüchen angesprochen - und das bereits zu günstigen Preisen.
        </p>
        <p className="mb-4">
          Da Familien häufig höhere monatliche Ausgaben haben, können alle Modelle
          der 2er BMW Reihe einfach und schnell geleast werden. Der große Vorteil
          besteht in den attraktiven Konditionen, die zu einer moderaten
          finanziellen Belastung führen.
        </p>
        <p>
          Bei der Motorisierung gibt es – je nach Fahrzeugtyp - eine große Auswahl
          von etwas über 100 bis zu rund 410 PS. Alle Modelle der 2er Reihe
          überzeugen uneingeschränkt durch ihre hervorragende Ausstattung. Mit
          unseren
          {" "}
          <Link href="/suche?marke=BMW&modell=2er" className="link link--secondary">
            günstigen Leasingangeboten ohne Anzahlung
          </Link>
          {" "}
          können sich zahlreiche Autofahrer selbst vom großen Platzangebot bei den
          Tourern sowie vom Fahrspaß bei allen vier Typen überzeugen.
        </p>
      </div>
    </div>
  );
}

export default BMW2er;
